<template>
    <div class="reservation-programs">
        <div class="route-page-content breadcrumb-content">
            <SidebarChild
                :items="menuItems"
                :selected-item="currentPage"
                :focusActive="true"
                @selected="fnClicked"
                @clicked="fnClicked"
            />
            <div class="page-content-wrapper">
                <div class="header-container">
                    <h2>{{ $t("calendar.reservationPrograms")}}</h2>
                    <SupsisButton primary @click="addNewProgram">{{ $t("calendar.addReservationProgram")}}</SupsisButton>
                </div>
                <div class="programs-groups">
                    <div v-for="(group, userId) in groupedPlans" :key="userId" class="group-container">
                        <div class="group-header" @click="toggleGroup(userId)">
                            <AgentAvatar :userID="userId" :size="32" />
                            <span>{{ getGroupName(userId) }}</span>
                            <a-icon :type="openGroups[userId] ? 'up' : 'down'" class="toggle-icon" />
                        </div>
                        <div v-show="openGroups[userId]" class="programs-grid">
                            <div
                                v-for="plan in group"
                                :key="plan._id"
                                class="program-card"
                                @click="goToPlanDetails(plan)"
                            >
                                <h3>{{ plan.title }}</h3>
                                <p class="d-flex align-center gap-5">
                                    <i class="material-icons">timer</i>
                                    {{ $t("report.duration") }}: {{ plan.duration }} {{ $t("report.minute")}}
                                </p>
                                <div class="card-actions">
                                    <SupsisButton @click.stop="viewPlan(plan)">Aç
                                    </SupsisButton>
                                    <template v-if="canEditPlan(plan)">
                                        <SupsisButton primary @click.stop="editPlan(plan)">{{ $t("message.edit") }}
                                        </SupsisButton>
                                        <SupsisButton danger @click.stop="confirmDeletePlan(plan)">{{ $t("message.delete")
                                            }}
                                        </SupsisButton>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { CALENDAR_API } from "../../../../api/calendar";
import SidebarChild from "../../../../components/Sidebar/SidebarChild.vue";
import calendarMenuItems from "./calendarMenuItems";
import AgentAvatar from "../../../../components/common/AgentAvatar.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../config/config";

export default Vue.extend({
    name: "ReservationPrograms",
    components: {
        SidebarChild,
        AgentAvatar,
        SupsisButton,
    },
    data() {
        return {
            reservationPlans: [],
            currentPage: "reservationPrograms",
            menuItems: calendarMenuItems,
            openGroups: {},
        };
    },
    computed: {
        ...mapState({
            currentUser: (state) => state.auth.user,
            isRoot: (state) => _.get(state, "auth.user.role.isRoot", false),
            site: (state) => state.site.site,
        }),
        groupedPlans() {
            return this.reservationPlans.reduce((acc, plan) => {
                const userId = plan.userId ? plan.userId._id : "unknown";
                if (!acc[userId]) {
                    acc[userId] = [];
                }
                acc[userId].push(plan);
                return acc;
            }, {});
        },
    },
    mounted() {
        this.getReservationPlans();
    },
    methods: {
        async getReservationPlans() {
            try {
                this.reservationPlans = await CALENDAR_API.getReservationPlans();
                // Tüm grupları başlangıçta açık olarak ayarla
                this.openGroups = Object.keys(this.groupedPlans).reduce((acc, userId) => {
                    acc[userId] = true;
                    return acc;
                }, {});
            } catch (error) {
                console.error(this.$t("calendar.resPlanError"), error);
            }
        },
        editPlan(plan) {
            this.$router.push(`/reservation/${plan._id}`);
        },
        confirmDeletePlan(plan) {
            this.$confirm({
                title: this.$t("calendar.resDeleteSure"),
                onOk: () => this.deletePlan(plan),
            });
        },
        async deletePlan(plan) {
            try {
                await CALENDAR_API.deleteReservationPlan(plan._id);
                this.reservationPlans = this.reservationPlans.filter((p) => p._id !== plan._id);
                this.$message.success(this.$t("calendar.resDeleteSuccess"));
            } catch (error) {
                console.error(this.$t("calendar.resDeleteError"), error);
                this.$message.error(this.$t("calendar.resDeleteError"));
            }
        },
        fnClicked(item) {
            if (item.key === "calendar") {
                this.$router.push("/calendar");
            } else if (item.key === "reservationBuilder") {
                this.$router.push("/calendar/reservation-builder");
            }
        },
        getGroupName(userId) {
            const user = this.reservationPlans.find((plan) => plan.userId && plan.userId._id === userId)?.userId;
            return user ? user.name : this.$t("calendar.unknownUser");
        },
        toggleGroup(userId) {
            this.$set(this.openGroups, userId, !this.openGroups[userId]);
        },
        goToPlanDetails(plan) {
            if (this.canEditPlan(plan)) {
                this.$router.push(`/reservation/${plan._id}`);
            }
        },
        addNewProgram() {
            this.$router.push('/reservation');
        },
        canEditPlan(plan) {
            // Root kullanıcı her şeyi düzenleyebilir
            if (this.isRoot) {
                return true;
            }

            // Plan sahibi kendi planını düzenleyebilir
            const planUserId = plan.userId ? plan.userId._id : null;
            const currentUserId = this.currentUser ? this.currentUser._id : null;

            return planUserId === currentUserId;
        },
        viewPlan(plan) {
            const reservationId = plan._id;
            const reservationURL = `${CONFIG.CALENDAR_URL}/?reservationId=${reservationId}&siteId=${this.site._id}&userId=${this.currentUser._id}`;
            window.open(reservationURL, "_blank");
        },
    },
});
</script>

<style scoped lang="scss">
.reservation-programs {
    .route-page-content {
        display: flex;
        height: 100%;
    }

    .page-content-wrapper {
        flex-grow: 1;
        padding: 20px;
    }

    .programs-groups {
        margin-top: 20px;
    }

    .group-container {
        margin-bottom: 20px;
        background-color: #f0f2f5;
        border-radius: 4px;
        overflow: hidden;
    }

    .group-header {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 16px;
        background-color: #e6e6e6;
        cursor: pointer;
        font-weight: bold;

        .toggle-icon {
            margin-left: auto;
        }
    }

    .programs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 20px;
        background-color: #ffffff;
        padding: 16px;
    }

    .program-card {
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 16px;
        background-color: #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
        transition: box-shadow 0.3s ease, transform 0.3s ease;
        cursor: pointer;

        &:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            transform: translateY(-2px);
            border: 1px solid rgba(0, 0, 0, 1);
        }

        h3 {
            margin-bottom: 10px;
        }

        p {
            margin-bottom: 5px;
        }

        .card-actions {
            display: flex;
            justify-content: space-between;
            margin-top: 15px;
            gap: 8px; // Butonlar arasında boşluk ekler
            flex-wrap: wrap;
        }
    }

    .header-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }
}
</style>
